import {
  CustomFieldEntityEnum,
  ICustomFieldResSchema,
  InvoiceSummaryResp,
  NET_TERMS_DISPLAY,
} from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterTypeOperator,
  TDataTablePager,
} from '@monetize/types/common';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../components/Monetize/DataTableNew/appTable';
import { useEffect, useState } from 'react';
import { useGetAllInvoices } from '../../../api/invoiceService';
import { SELECTION_COL } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  addressBodyTemplate,
  contactBodyTemplate,
  currencyBodyTemplate,
  dateBodyTemplate,
  datePeriodBodyTemplate,
  dateTimeBodyTemplate,
  emailListBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getCustomFieldsColumnGroup,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import {
  AccountLookupPopover,
  BillGroupLookupPopover,
  InvoiceLookupPopover,
} from '../../../components/Monetize/LookupPopover';
import { useCustomFields } from '../../../hooks/useCustomFields';
import { buildFilterParamsRequestObject } from '../../../utils';

const columnHelper = createColumnHelper<InvoiceSummaryResp>();

const COLUMNS_BASE: AppColumnDef<any, any>[] = [
  {
    ...SELECTION_COL,
  },
  columnHelper.group({
    id: 'invoiceDetails',
    header: 'Invoice Details',
    columns: columnHelper.columns([
      columnHelper.accessor('invoiceNumber', {
        ...getColHeaderWithSize('Invoice Number', 190),
        ...getColHeaderFilters('SET'),
        cell: ({ row }) => (
          <InvoiceLookupPopover
            value={row.original.id}
            displayLabel={row.original.invoiceNumber ?? undefined}
          />
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('account.accountName', {
        ...getColHeaderWithSize('Account', 250),
        ...getColHeaderFilters('SET'),
        cell: ({ row }) => (
          <AccountLookupPopover
            value={row.original.account?.id ?? ''}
            displayLabel={row.original.account?.accountName}
          />
        ),
        enableSorting: true,
        sortFn: 'text',
      }),
      columnHelper.accessor('billGroup.id', {
        ...getColHeaderWithSize('Bill Group', 250),
        ...getColHeaderFilters('SET'),
        cell: ({ row }) => (
          <BillGroupLookupPopover
            value={row.original.billGroupId}
            displayLabel={row.original.billGroup?.name}
          />
        ),
        enableSorting: true,
        sortFn: 'text',
      }),
    ]),
  }),
  columnHelper.group({
    id: 'dates',
    header: 'Dates',
    columns: columnHelper.columns([
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created Date'),
        ...getColHeaderFilters('DATE'),
        enableSorting: true,
        cell: dateTimeBodyTemplate(),
        sortFn: 'alphanumeric',
      }),
      columnHelper.accessor('lastSent', {
        ...getColHeaderWithSize('Last Emailed Date'),
        ...getColHeaderFilters('DATE'),
        enableSorting: true,
        cell: dateTimeBodyTemplate(),
        sortFn: 'alphanumeric',
      }),
      columnHelper.accessor('billDate', {
        ...getColHeaderWithSize('Bill Date'),
        ...getColHeaderFilters('DATE'),
        enableSorting: true,
        cell: dateBodyTemplate(),
        sortFn: 'alphanumeric',
      }),
      columnHelper.accessor('dueDate', {
        ...getColHeaderWithSize('Due Date'),
        ...getColHeaderFilters('DATE'),
        enableSorting: true,
        cell: dateBodyTemplate(),
        sortFn: 'alphanumeric',
      }),
      columnHelper.accessor('netTerms', {
        ...getColHeaderWithSize('Net Terms'),
        ...getColHeaderFilters('SET'),
        cell: enumDisplayBodyTemplate({
          displayMap: NET_TERMS_DISPLAY,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
      columnHelper.accessor('startDate', {
        ...getColHeaderWithSize('Period', 225),
        enableSorting: false,
        cell: datePeriodBodyTemplate({
          endPeriodProperty: 'endDate',
        }),
        enableColumnFilter: false,
      }),
    ]),
  }),
  columnHelper.group({
    id: 'amounts',
    header: 'Amounts',
    columns: columnHelper.columns([
      columnHelper.accessor('amount', {
        ...getColHeaderWithSize('Total Amount', 225),
        ...getColHeaderFilters('SET'),
        enableSorting: true,
        cell: currencyBodyTemplate({
          currencyProperty: 'currency',
        }),
      }),
      columnHelper.accessor('amountDue', {
        ...getColHeaderWithSize('Amount Due', 225),
        ...getColHeaderFilters('SET'),
        enableSorting: true,
        cell: currencyBodyTemplate({
          currencyProperty: 'currency',
        }),
      }),
    ]),
  }),
  columnHelper.group({
    id: 'contactInformation',
    header: 'Contact Information',
    columns: columnHelper.columns([
      columnHelper.accessor('billingContact', {
        ...getColHeaderWithSize('Billing Contact', 250),
        cell: contactBodyTemplate(),
        enableSorting: false,
      }),
      columnHelper.accessor('billGroup.ccEmails', {
        ...getColHeaderWithSize('CC Emails', 250),
        cell: emailListBodyTemplate(),
        enableSorting: false,
      }),
      columnHelper.accessor('billingAddress', {
        ...getColHeaderWithSize('Billing Address', 250),
        cell: addressBodyTemplate(),
        enableSorting: false,
      }),
      columnHelper.accessor('shippingContact', {
        ...getColHeaderWithSize('Shipping Contact', 250),
        cell: contactBodyTemplate(),
        enableSorting: false,
      }),
      columnHelper.accessor('shippingAddress', {
        ...getColHeaderWithSize('Shipping Address', 250),
        cell: addressBodyTemplate(),
        enableSorting: false,
      }),
    ]),
  }),
  columnHelper.group({
    id: 'customerValues',
    header: 'Customer Values',
    columns: columnHelper.columns([
      columnHelper.accessor('purchaseOrderNumber', {
        ...getColHeaderWithSize('PO Number'),
        ...getColHeaderFilters('SET'),
        cell: textBodyTemplate(),
        enableSorting: true,
        sortFn: 'text',
      }),
      columnHelper.accessor('registrationNumber', {
        ...getColHeaderWithSize('Registration Number'),
        ...getColHeaderFilters('SET'),
        cell: textBodyTemplate(),
        enableSorting: true,
        sortFn: 'text',
      }),
      columnHelper.accessor('vatNumber', {
        ...getColHeaderWithSize('VAT Number'),
        ...getColHeaderFilters('SET'),
        meta: { filterVariant: 'SET' },
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
    ]),
  }),
];

const PAGER: TDataTablePager = {
  ...DEFAULT_PAGER,
  first: 100,
};

function getColumnsFromCustomFields(
  customFields: ICustomFieldResSchema[] = [],
) {
  if (!customFields || customFields.length === 0) {
    return COLUMNS_BASE;
  }
  const newColumns = [...COLUMNS_BASE];
  const accountCustomFields = customFields.filter(
    (field) => field.entity === CustomFieldEntityEnum.ACCOUNT,
  );
  const billGroupCustomFields = customFields.filter(
    (field) => field.entity === CustomFieldEntityEnum.BILL_GROUP,
  );
  const invoiceCustomFields = customFields.filter(
    (field) => field.entity === CustomFieldEntityEnum.INVOICE,
  );
  if (invoiceCustomFields.length) {
    newColumns.push(
      getCustomFieldsColumnGroup({
        id: 'invoice.customFields',
        header: 'Invoice Custom Fields',
        accessorPathPrefix: 'customFields',
        customFieldMetadata: invoiceCustomFields,
      }),
    );
  }

  if (billGroupCustomFields.length) {
    newColumns.push(
      getCustomFieldsColumnGroup({
        id: 'billGroup.customFields',
        header: 'Bill Group Custom Fields',
        accessorPathPrefix: 'billGroup.customFields',
        customFieldMetadata: billGroupCustomFields,
      }),
    );
  }

  if (accountCustomFields.length) {
    newColumns.push(
      getCustomFieldsColumnGroup({
        id: 'account.customFields',
        header: 'Account Custom Fields',
        accessorPathPrefix: 'account.customFields',
        customFieldMetadata: accountCustomFields,
      }),
    );
  }
  return newColumns;
}

export const useInvoiceManagement = () => {
  // Server side filters to fetch data
  const [filters] = useState(
    buildFilterParamsRequestObject([
      {
        key: 'status',
        value: 'UNPAID',
        operator: FilterTypeOperator.EQUAL,
      },
    ]),
  );

  const customFieldQueryResult = useCustomFields(
    [
      CustomFieldEntityEnum.ACCOUNT,
      CustomFieldEntityEnum.BILL_GROUP,
      CustomFieldEntityEnum.INVOICE,
    ],
    true,
    true,
    {
      refetchOnWindowFocus: false,
    },
  );

  const [columns, setColumns] = useState<AppColumnDef<any, any>[]>(() =>
    getColumnsFromCustomFields(customFieldQueryResult.customFieldList),
  );
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (customFieldQueryResult.customFieldList) {
      setColumns(
        getColumnsFromCustomFields(customFieldQueryResult.customFieldList),
      );
    }
  }, [customFieldQueryResult.customFieldList]);

  const invoiceQueryResult = useGetAllInvoices(
    { config: PAGER, filters, onProgress: setProgress },
    { refetchOnWindowFocus: false },
  );

  return {
    customFieldQueryResult,
    invoiceQueryResult,
    columns,
    progress,
  };
};
